import React, { useState } from 'react';
import BlueButton from '../components/BlueButton';
import { v4 as uuidv4 } from 'uuid';
import { useAuthContext } from '../whitaijiFrontendLibrary/auth/hooks/useAuthContext';
import { isGodMode, NoAccess } from '../utils/permissions';
import { newSongsData } from '../data/newSongsData';
import { useStateContext } from '../context/StateContext';
import { _UPLOAD } from '../utils/patches';
import { AuthContext } from '../context/AuthContext';

const AiAudioAnalysis = () => {
  const { apiUrl, hideTestButtons } = useStateContext();
  const [audioFile, setAudioFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const {user} = useAuthContext(AuthContext)
  const songData = newSongsData[0]

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('audio/')) {
      setAudioFile(file);
      setError(null);
    } else {
      setError('Please upload a valid audio file');
      setAudioFile(null);
    }
  };

  const analyzeLyrics = async () => {
    if (!audioFile || !songData) {
      setError('Please provide both an audio file and ensure song data is loaded');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Create FormData with audio file and song data
      const formData = new FormData();
      formData.append('file', audioFile);
      formData.append('songData', JSON.stringify({
        title: songData?.title,
        lyrics: songData?.lyrics?.map(line => line.text)
      }));

      // Send to OpenAI for analysis using _UPLOAD utility
      const analysisData = await _UPLOAD({
        route: `${apiUrl}/openai/audio/analyze-lyrics`,
        formData,
        user,
        onError: (error) => {
          process.env.NODE_ENV === "development" && console.error('Upload error:', error);
          throw error;
        }
      });
      
      // Format the result to match the expected structure
      const formattedLyrics = songData.lyrics?.map((line, index) => ({
        ...line,
        time: analysisData.timestamps[index] || line.time // fallback to original timing if no match
      }));

      const result = {
        title: songData.title,
        soundSource: songData.soundSource,
        lyrics: formattedLyrics
      };

      setResult(result);
      process.env.NODE_ENV === "development" && console.log('Analysis complete:', result);

    } catch (err) {
      process.env.NODE_ENV === "development" && console.error('Error analyzing audio:', err);
      if (err.name === 'TypeError' && err.message === 'Failed to fetch') {
        setError('Network error. Please check your connection and try again.');
      } else if (err.response?.status === 413) {
        setError('Audio file is too large. Please use a file smaller than 25MB.');
      } else if (err.response?.status === 415) {
        setError('Unsupported audio format. Please use MP3, MP4, WAV, or WebM.');
      } else if (err.response?.status === 401) {
        setError('Authentication error. Please try logging in again.');
      } else {
        setError(err.message || 'Error analyzing audio file');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (!isGodMode(user)) {
    return <NoAccess />
  }

  return (
    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
      <h1>AI Audio Analysis</h1>
      
      <div style={{ marginTop: '20px' }}>
        <div style={{ marginBottom: '20px' }}>
          <h3>Step 1: Upload Audio File</h3>
          <input
            type="file"
            accept="audio/*"
            onChange={handleFileChange}
            style={{ marginBottom: '10px' }}
          />
        </div>

        <div style={{ marginBottom: '20px' }}>
          <h3>Step 2: Song Data</h3>
          <div>Title: {songData?.title}</div>
          <div>Total Lyrics: {songData?.lyrics?.length || 0} lines</div>
        </div>
        
        <div>
          <BlueButton
            text={isLoading ? "Analyzing..." : "Analyze Audio"}
            fn={analyzeLyrics}
            disabled={!audioFile || isLoading}
          />
        </div>

        {error && (
          <div style={{ 
            marginTop: '20px',
            padding: '10px',
            backgroundColor: '#fee2e2',
            color: '#dc2626',
            borderRadius: '4px'
          }}>
            {error}
          </div>
        )}

        {result && (
          <div style={{ 
            marginTop: '20px',
            padding: '20px',
            backgroundColor: '#f3f4f6',
            borderRadius: '8px',
            maxHeight: '400px',
            overflowY: 'auto'
          }}>
            <h2>Analysis Result</h2>
            <pre style={{ whiteSpace: 'pre-wrap' }}>
              {JSON.stringify(result, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
};

export default AiAudioAnalysis;
